import type { DbExecutor } from "./executor";
import { ValidationError } from "./repositories/projectRepository";
import type { RoomRepository } from "./repositories/roomRepository";
import type { FeatureRepository } from "./repositories/featureRepository";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import type { DesignScopeCoordinator } from "./designScopeCoordinator";
import { isChangeBinding, isObjectBinding, isOpeningReplacementBinding, isRoomBinding, type ProjectFeature } from "../domain/feature";
import { rectangleAreaX100, rectanglePerimeterX100, type Room, type RoomInput } from "../domain/room";

/**
 * MAKE ANOTHER ROOM LIKE THIS ONE (spec §90-12).
 *
 * The second bedroom is the first bedroom with different dimensions. What
 * copies is the RENOVATION CONFIGURATION — which work, which
 * specifications, which assembly answers — and nothing else:
 *
 *   COPIED      the room's type and notes; every piece of room scope with
 *               its action and notes; each work item's assemblies as fresh
 *               copy-on-use instances with the same option answers,
 *               component inclusions, quantity overrides and unit costs;
 *               a quantity that represents a CHOICE (2 devices, 1 door);
 *               room-measured bindings, which re-measure the NEW room.
 *   NOT COPIED  money and history of every kind — expenses, purchases,
 *               quotes, actuals, schedule state, baselines; anything owned
 *               by Design (objects, plan changes, door replacements,
 *               annotations, geometry); and the §82 project finishes, which
 *               the new room inherits from the project rather than owning.
 *
 * Geometry is deliberately NOT duplicated: a new room is a new space, so
 * the caller states its size (or leaves it blank), and every room-measured
 * quantity follows the new room's own measurements.
 */

export interface RoomCopyRequest {
  name: string;
  /** Blank leaves the new room unmeasured; the scope then waits for a size. */
  lengthX100?: number | null;
  widthX100?: number | null;
  floorAreaX100?: number | null;
  perimeterX100?: number | null;
  ceilingHeightInches?: number | null;
  /** Copy the source room's measurements instead of stating new ones. */
  copyMeasurements?: boolean;
}

export interface RoomCopyResult {
  room: Room;
  copiedFeatureIds: string[];
  /** Work left behind, with the reason — never copied silently. */
  skipped: Array<{ featureName: string; reason: string }>;
}

export class RoomCopyService {
  constructor(
    private readonly db: DbExecutor,
    private readonly rooms: RoomRepository,
    private readonly features: FeatureRepository,
    private readonly assemblies: AssemblyRepository,
    private readonly designScope: DesignScopeCoordinator,
  ) {}

  async copyRoom(sourceRoomId: string, request: RoomCopyRequest): Promise<RoomCopyResult> {
    const source = await this.rooms.getById(sourceRoomId);
    if (!source) throw new ValidationError(["That room no longer exists."]);
    if (request.name.trim() === "") throw new ValidationError(["Give the new room a name."]);

    const measurements: Pick<RoomInput, "floorAreaX100" | "perimeterX100" | "ceilingHeightInches" | "lengthX100" | "widthX100"> =
      request.copyMeasurements
        ? {
            floorAreaX100: source.floorAreaX100,
            perimeterX100: source.perimeterX100,
            ceilingHeightInches: source.ceilingHeightInches,
            lengthX100: source.lengthX100,
            widthX100: source.widthX100,
          }
        : {
            // One formula, wherever a room's size is stated (spec §89): given
            // length and width, the area and perimeter follow.
            floorAreaX100:
              request.floorAreaX100 ??
              (request.lengthX100 != null && request.widthX100 != null ? rectangleAreaX100(request.lengthX100, request.widthX100) : null),
            perimeterX100:
              request.perimeterX100 ??
              (request.lengthX100 != null && request.widthX100 != null ? rectanglePerimeterX100(request.lengthX100, request.widthX100) : null),
            ceilingHeightInches: request.ceilingHeightInches ?? source.ceilingHeightInches,
            lengthX100: request.lengthX100 ?? null,
            widthX100: request.widthX100 ?? null,
          };

    const projectId = await this.projectIdOf(sourceRoomId);
    const sourceFeatures = (await this.features.listByProject(projectId)).filter(
      (f) => f.archivedAt === null && f.roomId === sourceRoomId,
    );
    const result: RoomCopyResult = { room: source, copiedFeatureIds: [], skipped: [] };

    await this.db.transaction(async () => {
      const room = await this.rooms.create(source.floorId, {
        name: request.name.trim(),
        roomTypeId: source.roomTypeId,
        notes: source.notes,
        ...measurements,
      });
      result.room = room;

      for (const feature of sourceFeatures) {
        const reason = this.skipReason(feature);
        if (reason) {
          result.skipped.push({ featureName: feature.name, reason });
          continue;
        }
        const copy = await this.features.create(projectId, {
          designId: feature.designId,
          roomId: room.id,
          floorId: null,
          featureDefinitionId: feature.featureDefinitionId,
          name: feature.name,
          action: feature.action,
          // A typed quantity is a CHOICE ("2 outlets", "1 door") and comes
          // with the work; a room-measured one is re-measured below.
          quantityX100: feature.quantityGeometryBinding === null ? feature.quantityX100 : null,
          quantityUnit: feature.quantityGeometryBinding === null ? feature.quantityUnit : null,
          notes: feature.notes,
        });
        if (isRoomBinding(feature.quantityGeometryBinding)) {
          // The NEW room's measurements drive it (and attach the single
          // priced default only if the source had no assembly to copy).
          await this.designScope.bindFeatureQuantity(copy.id, feature.quantityGeometryBinding, { attachDefaultAssembly: false });
        }
        await this.copyAssemblies(feature.id, copy.id);
        try {
          await this.assemblies.regenerateForFeature(copy.id);
        } catch {
          /* an unresolvable assembly is reported by the estimate, never fatal */
        }
        result.copiedFeatureIds.push(copy.id);
      }
    });
    return result;
  }

  /**
   * Work that belongs to somebody else's system, with the reason. (§82
   * project finishes never appear here: they are project-level features
   * covering every room they apply to, so the new room inherits them.)
   */
  private skipReason(feature: ProjectFeature): string | null {
    if (isObjectBinding(feature.quantityGeometryBinding)) {
      return "Measured from what is placed in Design — draw it in the new room to scope it there.";
    }
    if (isChangeBinding(feature.quantityGeometryBinding)) {
      return "Comes from the plan's own changes (walls, openings), not from the room's configuration.";
    }
    if (isOpeningReplacementBinding(feature.quantityGeometryBinding)) {
      return "Counted from the doors drawn in Design.";
    }
    return null;
  }

  private async projectIdOf(roomId: string): Promise<string> {
    const rows = await this.db.select<{ project_id: string }>(
      "SELECT f.project_id FROM rooms r JOIN floors f ON f.id = r.floor_id WHERE r.id = ?",
      [roomId],
    );
    if (rows.length === 0) throw new ValidationError(["That room's project no longer exists."]);
    return rows[0].project_id;
  }

  /**
   * The same assemblies, as FRESH copy-on-use instances (Phase 4): attach
   * the template, then carry across the answers that made the source
   * instance what it is. Nothing is shared between the two rooms.
   */
  private async copyAssemblies(sourceFeatureId: string, targetFeatureId: string): Promise<void> {
    const instances = (await this.assemblies.listByFeature(sourceFeatureId)).filter((i) => i.archivedAt === null);
    for (const instance of instances) {
      if (!instance.templateId) continue; // a one-off custom assembly stays where it was built
      const copy = await this.assemblies.attachTemplate(targetFeatureId, instance.templateId);
      const target = await this.assemblies.getInstance(copy.id);
      if (!target) continue;
      for (const option of instance.options) {
        const match = target.options.find((o) => o.prompt === option.prompt);
        if (match && match.value !== option.value) await this.assemblies.setOption(copy.id, match.id, option.value);
      }
      for (const component of instance.components) {
        if (component.archivedAt !== null) continue;
        const match = target.components.find((c) => c.name === component.name && c.archivedAt === null);
        if (!match) continue;
        if (match.included !== component.included) await this.assemblies.setComponentIncluded(copy.id, match.id, component.included);
        if (component.qtyOverrideX100 !== null) await this.assemblies.setQtyOverride(copy.id, match.id, component.qtyOverrideX100);
        if (component.unitCostCents !== match.unitCostCents || component.laborMinutesX100 !== match.laborMinutesX100) {
          // A price somebody edited on the source instance is part of the
          // configuration; everything else about the component is the
          // template's own copy, passed through unchanged.
          await this.assemblies.updateComponent(copy.id, match.id, {
            name: match.name,
            componentType: match.componentType,
            role: match.role,
            inclusion: match.inclusion,
            conditionOptionId: match.conditionOptionId,
            qtyBasis: match.qtyBasis,
            fixedQtyX100: match.fixedQtyX100,
            multiplierX10000: match.multiplierX10000,
            wastePctX100: match.wastePctX100,
            unit: match.unit,
            unitCostCents: component.unitCostCents,
            laborMinutesX100: component.laborMinutesX100,
            taxable: match.taxable,
            categoryId: match.categoryId,
            materialDefinitionId: match.materialDefinitionId,
            notes: match.notes,
          });
        }
      }
    }
  }
}
